// Which legal documents exist, what version each is on, and how a signup-time
// acceptance survives until there is a session to record it against.
//
// The version strings are the contract with infra/supabase/migrations/
// 20260922000200_legal_acceptance.sql: an acceptance row stores the exact
// version the user was shown. Change the wording of /legal/terms and you must
// bump TERMS_VERSION here, or the ledger will claim people agreed to text they
// never saw.
//
// Pure — no supabase import — so the rules are testable. The RPC call lives in
// ./legal-client.ts.

export type LegalDocument = "terms" | "privacy";

/** ISO date the text was last changed. Bump when the wording changes. */
export const TERMS_VERSION = "2026-09-22";
export const PRIVACY_VERSION = "2026-10-02";

export const LEGAL_DOCUMENT_VERSIONS: Record<LegalDocument, string> = {
  terms: TERMS_VERSION,
  privacy: PRIVACY_VERSION,
};

/** Customer-facing support address. Shown in-app and in legal/contact copy. */
export const SUPPORT_EMAIL = "support@mockingbird.digital";

export const LEGAL_DOCUMENTS: ReadonlyArray<{
  document: LegalDocument;
  title: string;
  path: string;
}> = [
  { document: "terms", title: "Terms of Service", path: "/legal/terms" },
  { document: "privacy", title: "Privacy Policy", path: "/legal/privacy" },
];

/**
 * The draft banner text, kept in one place so the two pages cannot drift and
 * so nobody can quietly delete it from one of them. These documents have not
 * been through a lawyer.
 */
export const LEGAL_DRAFT_NOTICE =
  "Draft pending legal review. This text was written by the Mockingbird team, not by a lawyer, and has not been reviewed by one. Treat it as a description of how we intend to operate, and have it reviewed by an Australian legal practitioner before relying on it.";

// ─── Deferred acceptance ─────────────────────────────────────────────────────

/**
 * With email confirmation on, signup returns no session, so there is nobody to
 * record the acceptance against yet. The tick is parked in localStorage and
 * flushed the first time a session appears. localStorage rather than component
 * state because the user confirms in a different tab, often on a different day.
 */
export const PENDING_ACCEPTANCE_KEY = "mockingbird.pendingLegalAcceptance";

export interface PendingAcceptance {
  email: string;
  versions: Record<LegalDocument, string>;
  accepted_at: string;
}

/** Older than this and we ask again rather than recording a stale tick. */
const PENDING_ACCEPTANCE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The parked "email" for a sign-up that went to Google.
 *
 * The person ticks the box, then leaves for Google, and the account's email is
 * only known when they come back. So the tick is parked for whoever signs in
 * next in this browser, and only briefly: the round trip takes seconds, and a
 * short life keeps a shared browser from recording it for someone else later.
 */
export const OAUTH_PENDING_EMAIL = "oauth:google";
const OAUTH_PENDING_MAX_AGE_MS = 30 * 60 * 1000;

export function buildPendingAcceptance(email: string, now: Date): PendingAcceptance {
  return {
    email: email.trim().toLowerCase(),
    versions: { ...LEGAL_DOCUMENT_VERSIONS },
    accepted_at: now.toISOString(),
  };
}

export function serialisePendingAcceptance(pending: PendingAcceptance): string {
  return JSON.stringify(pending);
}

/**
 * @returns the acceptance to record, or null when there is nothing usable.
 * Deliberately total: a corrupt or foreign entry returns null rather than
 * throwing into a render.
 */
export function parsePendingAcceptance(
  raw: string | null | undefined,
  now: Date,
): PendingAcceptance | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as Partial<PendingAcceptance>;
  if (typeof candidate.email !== "string" || !candidate.email) return null;
  if (typeof candidate.accepted_at !== "string") return null;
  const acceptedAt = Date.parse(candidate.accepted_at);
  if (!Number.isFinite(acceptedAt)) return null;
  if (now.getTime() - acceptedAt > PENDING_ACCEPTANCE_MAX_AGE_MS) return null;
  if (candidate.email === OAUTH_PENDING_EMAIL && now.getTime() - acceptedAt > OAUTH_PENDING_MAX_AGE_MS) return null;
  const versions = candidate.versions;
  if (!versions || typeof versions !== "object") return null;
  if (typeof versions.terms !== "string" || typeof versions.privacy !== "string") return null;
  return {
    email: candidate.email.trim().toLowerCase(),
    versions: { terms: versions.terms, privacy: versions.privacy },
    accepted_at: candidate.accepted_at,
  };
}

/**
 * A parked acceptance only counts for the account it was made on. Signing in
 * as someone else on a shared browser must not record consent on their behalf.
 */
export function pendingAcceptanceApplies(
  pending: PendingAcceptance | null,
  sessionEmail: string | null | undefined,
): boolean {
  if (!pending) return false;
  const email = (sessionEmail ?? "").trim().toLowerCase();
  if (pending.email === OAUTH_PENDING_EMAIL) return email.length > 0;
  return email.length > 0 && email === pending.email;
}
